export type PlotImageFormat = "png" | "svg";

function dataUrlToBlob(dataUrl: string): Blob {
  const comma = dataUrl.indexOf(",");
  const header = dataUrl.slice(0, comma);
  const payload = dataUrl.slice(comma + 1);
  const mime = header.slice(5).split(";")[0] || "application/octet-stream";

  // Plotly returns SVG as URL-encoded text, every other format as base64
  if (!header.includes(";base64")) {
    return new Blob([decodeURIComponent(payload)], {type: mime});
  }

  const binary = atob(payload);
  const bytes = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);

  return new Blob([bytes], {type: mime});
}

/**
 * Renders a copy of the plot offscreen with WebGL traces converted to SVG ones.
 * Plotly rasterises scattergl into an embedded bitmap, which would defeat an SVG
 * export of the score figures (see base/views/plots/_common.py).
 */
async function toVectorSvg(plotEl: any, Plotly: any): Promise<string> {
  const {width, height} = plotEl.getBoundingClientRect();
  const data = (plotEl.data as any[]).map(trace =>
    trace.type === "scattergl" ? {...trace, type: "scatter"} : trace
  );
  const layout = {...plotEl.layout, width, height, autosize: false};

  const tmp = document.createElement("div");
  tmp.style.cssText = "position:absolute;left:-9999px;top:0;visibility:hidden";
  document.body.appendChild(tmp);

  try {
    await Plotly.newPlot(tmp, data, layout, {staticPlot: true});

    return await Plotly.toImage(tmp, {format: "svg", width, height});
  } finally {
    Plotly.purge(tmp);
    tmp.remove();
  }
}

/** Renders the given plot to an image blob. Called with the graph div a modebar button receives. */
export async function plotImageBlob(plotEl: any, format: PlotImageFormat): Promise<Blob> {
  const Plotly = (window as any).Plotly;

  if (!plotEl?.data || !Plotly) throw new Error("Plot not found");

  const dataUrl = format === "svg"
    ? await toVectorSvg(plotEl, Plotly)
    : await Plotly.toImage(plotEl, {format: "png", scale: 3});

  return dataUrlToBlob(dataUrl);
}

/** The download slug the backend bakes into the figure's config (see base/utils.py). */
export function plotFileName(plotEl: any): string {
  return plotEl?._context?.toImageButtonOptions?.filename || "thecellmap_human_plot";
}
